// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the host's side of a meeting (`docs/SHARED_AR_PLAN.md`): the key
 * that lets this browser start one, and the call that does.
 *
 * Hosting is not public. A browser may host once it has been opened with
 * `?meetingKey=<key>`; the key is kept in this browser's storage and
 * taken out of the address bar, and `?meetingKey=off` forgets it. Without
 * a key there is no "Start a meeting" entry at all (the Tools menu asks
 * `hasMeetingKey`), the same shape as the geo-media switch. The key only
 * opens the door on this side: the server checks it on every request
 * (`functions/api/meeting`), so a wrong key shows the entry and is then
 * refused.
 *
 * When hosting is sold, the key gives way to an account and its plan; the
 * panel and `createMeeting`'s result stay as they are.
 */

import { currentEntitlements } from './account'

const STORAGE_KEY = 'ayni-meeting-key'
const PARAM = 'meetingKey'

export interface MeetingLinks {
  /** The room code, for saying aloud. */
  code: string
  /** When the meeting and its links stop working, epoch ms. */
  expiresAt: number
  links: { presenter: string; moderator: string; audience: string }
  /** AYNI: the meeting lends everyone in it the data tools (an Education host, owner 2026-10-05). */
  lendsData?: boolean
}

export type MeetingCreateResult =
  | { ok: true; meeting: MeetingLinks }
  /** `unauthorized`: the key was refused. `unavailable`: meetings are not set up or the request failed. */
  | { ok: false; reason: 'unauthorized' | 'plan_cannot_host' | 'unavailable' }

function storage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/**
 * Take a `?meetingKey=` from a page address: store the key (or forget it
 * for `off`) and return the address without the parameter, or null when
 * there was none.
 */
export function takeMeetingKeyParam(href: string, store: Storage | null = storage()): string | null {
  const url = new URL(href)
  const value = url.searchParams.get(PARAM)
  if (value === null) return null
  const key = value.trim()
  try {
    if (!key || key === 'off') store?.removeItem(STORAGE_KEY)
    else store?.setItem(STORAGE_KEY, key)
  } catch {
    // Storage refused (private mode): the key lasts as long as nothing.
  }
  url.searchParams.delete(PARAM)
  return url.toString()
}

/** The stored host key, or null. */
export function meetingKey(store: Storage | null = storage()): string | null {
  try {
    const key = store?.getItem(STORAGE_KEY)?.trim()
    return key ? key : null
  } catch {
    return null
  }
}

/** Whether this browser has been given a host key. */
export function hasMeetingKey(): boolean {
  return meetingKey() !== null
}

const MEETINGS_KEY = 'ayni-meetings'

/**
 * Keep a created meeting's links in this browser, so the host can send
 * them again after stepping into the presenter's seat (the meeting line,
 * roomChip). The site keeps no list of meetings and the signed links are
 * shown nowhere else, so this browser's copy is the only one. Expired
 * meetings are dropped whenever one is added.
 */
export function rememberMeeting(meeting: MeetingLinks, store: Storage | null = storage(), now: number = Date.now()): void {
  try {
    const all = readMeetings(store)
    for (const code of Object.keys(all)) if (all[code].expiresAt <= now) delete all[code]
    all[meeting.code] = meeting
    store?.setItem(MEETINGS_KEY, JSON.stringify(all))
  } catch {
    // Storage refused: the links were shown once, as before.
  }
}

/** The links of meeting `code` created in this browser and not yet expired, or null. */
export function rememberedMeeting(code: string, store: Storage | null = storage(), now: number = Date.now()): MeetingLinks | null {
  const meeting = readMeetings(store)[code]
  return meeting && meeting.expiresAt > now ? meeting : null
}

function readMeetings(store: Storage | null): Record<string, MeetingLinks> {
  try {
    const raw = store?.getItem(MEETINGS_KEY)
    const parsed: unknown = raw ? JSON.parse(raw) : {}
    if (typeof parsed !== 'object' || parsed === null) return {}
    const out: Record<string, MeetingLinks> = {}
    for (const [code, value] of Object.entries(parsed as Record<string, unknown>)) {
      const m = value as Partial<MeetingLinks> | null
      const l = m?.links
      if (
        m && typeof m.code === 'string' && m.code === code && typeof m.expiresAt === 'number' && l &&
        typeof l.presenter === 'string' && typeof l.moderator === 'string' && typeof l.audience === 'string'
      ) {
        out[code] = { code, expiresAt: m.expiresAt, links: { presenter: l.presenter, moderator: l.moderator, audience: l.audience } }
      }
    }
    return out
  } catch {
    return {}
  }
}

/**
 * Whether this browser can start a meeting: it holds the owner's host key,
 * or is signed in to an account whose AYNI XR licence has a plan that hosts
 * (`account`, `xrPlans`). The server decides again on every request.
 */
export function canHostMeetings(): boolean {
  return hasMeetingKey() || currentEntitlements().meetings > 0
}

/**
 * Ask the site for a new meeting: with the owner's host key when this
 * browser has one, else as the signed-in account (its session cookie).
 */
export async function createMeeting(
  key: string | null = meetingKey(),
  fetchImpl: typeof fetch = fetch,
): Promise<MeetingCreateResult> {
  try {
    const headers: Record<string, string> = key ? { 'X-Meeting-Api-Key': key } : {}
    const res = await fetchImpl('/api/meeting', { method: 'POST', headers, credentials: 'same-origin' })
    if (res.status === 401) return { ok: false, reason: 'unauthorized' }
    if (res.status === 402) return { ok: false, reason: 'plan_cannot_host' }
    if (!res.ok) return { ok: false, reason: 'unavailable' }
    const body = (await res.json()) as Partial<MeetingLinks>
    const links = body.links
    if (
      typeof body.code !== 'string' ||
      typeof body.expiresAt !== 'number' ||
      !links ||
      typeof links.presenter !== 'string' ||
      typeof links.moderator !== 'string' ||
      typeof links.audience !== 'string'
    ) {
      return { ok: false, reason: 'unavailable' }
    }
    return { ok: true, meeting: { code: body.code, expiresAt: body.expiresAt, links, lendsData: body.lendsData === true } }
  } catch {
    return { ok: false, reason: 'unavailable' }
  }
}
